function capitalizeFirstLetter(string) {
  return string[0].toUpperCase() + string.slice(1);
}

/**
 * Build a map of resolver functions which return the "default" value
 * for each field
 *
 * @param {Object} i18n An i18next definition object - this is the format
 * we use to create translations in the schema
 * @param {String} typeNamePrefix Usually derived from an application
 * name (e.g. HotelBooking)
 */
function getResolvers(i18n, typeNamePrefix) {
  const { properties } = i18n.definitions.namespaces;
  const resolvers = {};
  const clientWarning = (key, parentType) => {
    if (process.env.NODE_ENV !== 'test') {
      console.log(
        `A @client directive has been used for "${key}" in "${parentType}". Ensure that this is migrated to the main schema before release to production.`
      );
    }
  };
  const makePropertyResolver = (properties, key) => {
    return obj => {
      clientWarning(key, obj.__typename);
      return properties[key].default;
    };
  };

  Object.keys(properties).forEach(namespace => {
    const nsProperties = properties[namespace].properties;
    const defaultField = properties[namespace].default;
    let returnValue;

    // Create resolvers for every attribute of each property if there isn't a global default.
    if (
      Object.keys(defaultField).length === 0 &&
      defaultField.constructor === Object
    ) {
      returnValue = {};

      for (var key in nsProperties) {
        returnValue[key] = makePropertyResolver(nsProperties, key);
      }
    }

    // Create a resolver for just the attribute itself.
    else {
      returnValue = (obj, args, context, info) => {
        return properties[namespace].default;
      };
    }

    resolvers[
      `${typeNamePrefix}${capitalizeFirstLetter(namespace)}Translations`
    ] = returnValue;
  });

  return resolvers;
}

/**
 * Build Schema Definition Language reflecting the types and fields from the
 * i18next defintions. This is used to ensure that the Apollo Client
 * and Dev Tools are fully aware of the local additions
 *
 * @param {Object} i18n An i18next definition object - this is the format
 * we use to create translations in the schema
 * @param {String} typeNamePrefix Usually derived from an application
 * name (e.g. HotelBooking)
 */
function getTypedefs(i18n, typeNamePrefix) {
  let { properties: types } = i18n.definitions.namespaces;

  const typeToDefs = (typeName, typeNamePrefix, fields) => {
    return `
      extend type ${typeNamePrefix}${capitalizeFirstLetter(
      typeName
    )}Translations {
        ${fields.map(field => `${field}: String\n`)}
      }
    `;
  };

  const buildTypes = types => {
    return Object.keys(types).map(typeName => {
      let fields = Object.keys(types[typeName].properties);
      return typeToDefs(typeName, typeNamePrefix, fields);
    });
  };

  return buildTypes(types);
}

/**
 * Make an object containing `typeDefs` and `resolvers` for use in Apollo Client
 *
 * @param {Object} i18n An i18next definition object - this is the format
 * we use to create translations in the schema
 * @param {String} typeNamePrefix Usually derived from an application
 * name (e.g. HotelBooking)
 */
export default function configureStagedTranslations(i18n, typeNamePrefix) {
  return {
    typeDefs: getTypedefs(i18n, typeNamePrefix),
    resolvers: getResolvers(i18n, typeNamePrefix),
  };
}
